import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { Mission } from '../../models/missions/mission.model';
import { MissionReward } from '../../models/missions/mission-reward.model';
import { MissionsFiltersRequest } from '../../models/missions/requests/api-missions-get-request.model';
import { MissionsResultsResponse } from '../../models/missions/responses/api-missions-results-response.model';
import { MissionRewardsResultsResponse } from '../../models/missions/responses/api-mission-rewards-results-response.model';
import { ApiMissionsService } from '../../services/missions/api/api-missions.service';
import { AlertsService } from '../../services/alerts.service';
import { ModalsService } from '../../services/modals.service';
import { DateService } from '../../services/date.service';
import { MissionType } from '../../enums/missions/mission-type.enum';
import { UserCurrencyType } from '../../enums/users/user-currency-type.enum';
import { MissionAddModalComponent } from './modals/mission-add-modal/mission-add-modal.component';
import { MissionEditModalComponent } from './modals/mission-edit-modal/mission-edit-modal.component';
import { MissionUsersModalComponent } from './modals/mission-users-modal/mission-users-modal.component';
import { MissionRewardAddModalComponent } from './modals/mission-reward-add-modal/mission-reward-add-modal.component';
import { MissionRewardEditModalComponent } from './modals/mission-reward-edit-modal/mission-reward-edit-modal.component';
import { ConfigurationsBlockComponent } from '../configurations/configurations-block/configurations-block.component';
import { ConfigurationDef } from '../configurations/configurations-block/configuration-def.model';

@Component({
  templateUrl: 'missions.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, TooltipModule, CollapseModule, TabsModule,
    MissionAddModalComponent, MissionEditModalComponent, MissionUsersModalComponent,
    MissionRewardAddModalComponent, MissionRewardEditModalComponent, ConfigurationsBlockComponent]
})
export class MissionsComponent implements OnInit {

  readonly missionsConfigurations: ConfigurationDef[] = [
    { name: 'game_missions_feature_active', label: 'Feature Active', type: 'bool' },
    { name: 'game_missions_level_unlocked', label: 'Level Unlocked', type: 'number' },
    { name: 'game_missions_claim_grace_period', label: 'Claim Grace Period', type: 'number' },
    { name: 'missions_clean_timeout', label: 'Clean Timeout', type: 'number' },
  ];

  @ViewChild('missionsFileInput') missionsFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('rewardsFileInput') rewardsFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('missionAddModal') missionAddModal!: MissionAddModalComponent;
  @ViewChild('missionEditModal') missionEditModal!: MissionEditModalComponent;
  @ViewChild('missionUsersModal') missionUsersModal!: MissionUsersModalComponent;
  @ViewChild('missionRewardAddModal') missionRewardAddModal!: MissionRewardAddModalComponent;
  @ViewChild('missionRewardEditModal') missionRewardEditModal!: MissionRewardEditModalComponent;

  readonly missionTypes = Object.entries(MissionType).filter(([, v]) => typeof v === 'number') as [string, number][];
  readonly currencyTypes = Object.entries(UserCurrencyType).filter(([, v]) => typeof v === 'number') as [string, number][];

  // Missions tab
  missions: Mission[] = [];
  missionsIsLoading: boolean = false;
  missionsTotalResults: number = 0;
  readonly missionsResultsPerPage: number = 25;
  missionsIsFilterOpen: boolean = false;
  missionsFilters: MissionsFiltersRequest = new MissionsFiltersRequest();
  missionsStartDateMinFilter: string = '';
  missionsStartDateMaxFilter: string = '';
  private static missionsPageNumber: number = 1;

  get missionsPageNumber(): number { return MissionsComponent.missionsPageNumber; }
  get missionsTotalPages(): number { return Math.ceil(this.missionsTotalResults / this.missionsResultsPerPage); }

  // Mission rewards tab
  missionRewards: MissionReward[] = [];
  rewardsIsLoading: boolean = false;
  rewardsTotalResults: number = 0;
  readonly rewardsResultsPerPage: number = 25;
  rewardsIsFilterOpen: boolean = false;
  rewardsFilters: MissionsFiltersRequest = new MissionsFiltersRequest();
  rewardsStartDateMinFilter: string = '';
  rewardsStartDateMaxFilter: string = '';
  private static rewardsPageNumber: number = 1;

  get rewardsPageNumber(): number { return MissionsComponent.rewardsPageNumber; }
  get rewardsTotalPages(): number { return Math.ceil(this.rewardsTotalResults / this.rewardsResultsPerPage); }

  constructor(
    public dateService: DateService,
    private apiMissionsService: ApiMissionsService,
    private alertsService: AlertsService,
    private modalsService: ModalsService
  ) {}

  ngOnInit(): void {
    this.refreshMissions();
  }

  onSelectMissionsTab(): void { this.refreshMissions(); }
  onSelectRewardsTab(): void { this.refreshMissionRewards(); }

  getMissionTypeName(type: number): string { return MissionType[type] ?? String(type); }
  getCurrencyTypeName(type: number): string { return UserCurrencyType[type] ?? String(type); }

  getCurrencyTypeDisplay(reward: MissionReward): string {
    const name = this.getCurrencyTypeName(reward.currencyType);
    if (reward.currencyType === UserCurrencyType.MatchTicket) {
      return `${name}(${reward.currencyTicketCost})`;
    }
    return name;
  }

  getPageNumbers(current: number, total: number): (number | null)[] {
    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);
    const sorted = Array.from(visibleSet).sort((a, b) => a - b);
    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }
    return result;
  }

  // --- Missions pagination & filters ---
  missionsGoToPage(page: number): void { MissionsComponent.missionsPageNumber = page; this.refreshMissions(); }
  onMissionsPaginationPrevious(): void { MissionsComponent.missionsPageNumber--; this.refreshMissions(); }
  onMissionsPaginationNext(): void { MissionsComponent.missionsPageNumber++; this.refreshMissions(); }

  onClickMissionsFilter(): void {
    this.missionsFilters.start_date_min_timestamp = this.missionsStartDateMinFilter ? Math.floor(new Date(this.missionsStartDateMinFilter).getTime() / 1000) : null;
    this.missionsFilters.start_date_max_timestamp = this.missionsStartDateMaxFilter ? Math.floor(new Date(this.missionsStartDateMaxFilter).getTime() / 1000) : null;
    MissionsComponent.missionsPageNumber = 1;
    this.refreshMissions();
  }

  onClickMissionsResetFilters(): void {
    this.missionsFilters = new MissionsFiltersRequest();
    this.missionsStartDateMinFilter = '';
    this.missionsStartDateMaxFilter = '';
    MissionsComponent.missionsPageNumber = 1;
    this.refreshMissions();
  }

  // --- Mission rewards pagination & filters ---
  rewardsGoToPage(page: number): void { MissionsComponent.rewardsPageNumber = page; this.refreshMissionRewards(); }
  onRewardsPaginationPrevious(): void { MissionsComponent.rewardsPageNumber--; this.refreshMissionRewards(); }
  onRewardsPaginationNext(): void { MissionsComponent.rewardsPageNumber++; this.refreshMissionRewards(); }

  onClickRewardsFilter(): void {
    this.rewardsFilters.start_date_min_timestamp = this.rewardsStartDateMinFilter ? Math.floor(new Date(this.rewardsStartDateMinFilter).getTime() / 1000) : null;
    this.rewardsFilters.start_date_max_timestamp = this.rewardsStartDateMaxFilter ? Math.floor(new Date(this.rewardsStartDateMaxFilter).getTime() / 1000) : null;
    MissionsComponent.rewardsPageNumber = 1;
    this.refreshMissionRewards();
  }

  onClickRewardsResetFilters(): void {
    this.rewardsFilters = new MissionsFiltersRequest();
    this.rewardsStartDateMinFilter = '';
    this.rewardsStartDateMaxFilter = '';
    MissionsComponent.rewardsPageNumber = 1;
    this.refreshMissionRewards();
  }

  // --- Mission actions ---
  onClickAddMissionsFromFile(): void { this.missionsFileInput.nativeElement.click(); }

  onMissionsFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.missionsIsLoading = true;
    this.apiMissionsService.addMissionsFromFile(file).then(
      () => {
        this.alertsService.displayAlertSuccess('Missions imported successfully!');
        this.refreshMissions();
      }, error => {
        this.missionsIsLoading = false;
        this.alertsService.displayAlertError('Error importing missions: ' + error);
      }
    );
    input.value = '';
  }

  onClickAddMissionsRewardsFromFile(): void { this.rewardsFileInput.nativeElement.click(); }

  onRewardsFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.rewardsIsLoading = true;
    this.apiMissionsService.addMissionsRewardsFromFile(file).then(
      () => {
        this.alertsService.displayAlertSuccess('Mission rewards imported successfully!');
        this.refreshMissionRewards();
      }, error => {
        this.rewardsIsLoading = false;
        this.alertsService.displayAlertError('Error importing mission rewards: ' + error);
      }
    );
    input.value = '';
  }

  onClickAddMission(): void {
    this.missionAddModal.show(() => this.refreshMissions());
  }

  onClickEditMission(mission: Mission): void {
    this.missionEditModal.show(mission, () => this.refreshMissions());
  }

  onClickMissionUsers(mission: Mission): void {
    this.missionUsersModal.show(mission.missionId);
  }

  onClickDeleteMission(mission: Mission): void {
    this.modalsService.showConfirmModal(() => {
      this.apiMissionsService.deleteMission(mission.missionId).then(
        () => {
          this.alertsService.displayAlertSuccess('Mission deleted !');
          this.refreshMissions();
        }, error => {
          this.alertsService.displayAlertError('Error deleting mission : ' + error);
        }
      );
    });
  }

  // --- Mission reward actions ---
  onClickAddMissionReward(): void {
    this.missionRewardAddModal.show(() => this.refreshMissionRewards());
  }

  onClickEditMissionReward(reward: MissionReward): void {
    this.missionRewardEditModal.show(reward, () => this.refreshMissionRewards());
  }

  onClickDeleteMissionReward(reward: MissionReward): void {
    this.modalsService.showConfirmModal(() => {
      this.apiMissionsService.deleteMissionReward(reward.missionRewardId).then(
        () => {
          this.alertsService.displayAlertSuccess('Mission reward deleted !');
          this.refreshMissionRewards();
        }, error => {
          this.alertsService.displayAlertError('Error deleting mission reward : ' + error);
        }
      );
    });
  }

  private refreshMissions(): void {
    this.missionsIsLoading = true;
    this.apiMissionsService.getMissions(MissionsComponent.missionsPageNumber, this.missionsResultsPerPage, this.missionsFilters).then(
      (response: MissionsResultsResponse) => {
        this.missionsIsLoading = false;
        this.missions = response.results;
        this.missionsTotalResults = response.resultsTotal;
      }, error => {
        this.missionsIsLoading = false;
        this.alertsService.displayAlertError('Error loading missions : ' + error);
      }
    );
  }

  private refreshMissionRewards(): void {
    this.rewardsIsLoading = true;
    this.apiMissionsService.getMissionsRewards(MissionsComponent.rewardsPageNumber, this.rewardsResultsPerPage, this.rewardsFilters).then(
      (response: MissionRewardsResultsResponse) => {
        this.rewardsIsLoading = false;
        this.missionRewards = response.results;
        this.rewardsTotalResults = response.resultsTotal;
      }, error => {
        this.rewardsIsLoading = false;
        this.alertsService.displayAlertError('Error loading mission rewards : ' + error);
      }
    );
  }
}
